/**
 * EPC stock counts from the lsk-rfid handheld app (LSK, 2026-11-02).
 *
 * One count may cover several locations, scanned in one sweep; each tag is credited to the
 * location the system places it at, so every location is reported on its own — expected, found,
 * missing — with what was found broken down by quality status.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';

import { api } from '../lib/api';
import { Card, Table, Loading, Empty, cell, when } from '../components/ui';

type CountSummary = {
  id: number;
  countNo: string;
  locations: string[];
  status: 'open' | 'submitted';
  createdBy: string;
  createdAt: string;
  submittedAt: string | null;
  scanned: number;
};

type SkuLine = {
  sku: string;
  expected: number;
  found: number;
  missing: number;
  quality: Record<string, number>;
};

type LocationReport = {
  location: string;
  name: string;
  expected: number;
  found: number;
  missing: number;
  quality: Record<string, number>;
  skus: SkuLine[];
  missingEpcs: string[];
};

type Report = {
  count: CountSummary;
  locations: LocationReport[];
  elsewhereTags: Array<{
    epc: string;
    sku: string;
    location: string;
    inStock: boolean;
    quality: string;
  }>;
  unknownEpcs: string[];
};

/** The quality labels that actually occur, in LSK's own order — eleven empty columns help nobody. */
function usedLabels(all: string[], records: Array<Record<string, number>>): string[] {
  return all.filter((l) => records.some((r) => (r[l] ?? 0) > 0));
}

export default function EpcCounts() {
  const { id } = useParams();
  return id ? <CountReport id={id} /> : <CountList />;
}

function CountList() {
  const list = useQuery({
    queryKey: ['epc-counts'],
    queryFn: () => api.get<{ counts: CountSummary[] }>('/api/v1/app/counts?limit=200'),
  });
  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold text-slate-900">EPC stock counts</h1>
      <Card
        title="Counts from the handheld app"
        subtitle="Each tag is compared one by one with what the system holds at the locations counted."
      >
        {list.isLoading ? (
          <Loading />
        ) : (
          <Table
            head={['Count', 'Locations', 'Status', 'Tags scanned', 'By', 'Started', 'Submitted']}
            rows={(list.data?.counts ?? []).map((c) => [
              <Link
                className="font-medium text-brand-700 hover:underline"
                to={`/rfid/epc-counts/${c.id}`}
              >
                {c.countNo}
              </Link>,
              c.locations.join(', '),
              c.status === 'submitted' ? (
                <span className="text-emerald-700">submitted</span>
              ) : (
                <span className="text-amber-700">in progress</span>
              ),
              cell(c.scanned, c.scanned.toLocaleString()),
              c.createdBy,
              when(c.createdAt),
              when(c.submittedAt),
            ])}
          />
        )}
      </Card>
    </div>
  );
}

function CountReport({ id }: { id: string }) {
  const [open, setOpen] = useState<string | null>(null);
  const [vsSql, setVsSql] = useState<string | null>(null);
  const statuses = useQuery({
    queryKey: ['quality-statuses'],
    queryFn: () =>
      api.get<{ statuses: Array<{ code: number; label: string }> }>('/api/v1/app/quality-statuses'),
    staleTime: Infinity,
  });
  const report = useQuery({
    queryKey: ['epc-count', id],
    queryFn: () => api.get<Report>(`/api/v1/app/counts/${encodeURIComponent(id)}/report`),
  });
  const r = report.data;
  const allLabels = (statuses.data?.statuses ?? []).map((s) => s.label);

  if (report.isLoading) return <Loading />;
  if (!r) return <Empty>No such count.</Empty>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <Link to="/rfid/epc-counts" className="text-sm text-brand-700 hover:underline">
            ← All EPC counts
          </Link>
          <h1 className="text-xl font-semibold text-slate-900">{r.count.countNo}</h1>
          <p className="text-sm text-slate-600">
            {r.count.locations.join(', ')} · {r.count.scanned.toLocaleString()} tags scanned · by{' '}
            {r.count.createdBy} ·{' '}
            {r.count.status === 'submitted'
              ? 'submitted — figures are as they stood at submit'
              : 'in progress — figures are live'}
          </p>
        </div>
        <div className="flex gap-2">
          <a
            className="rounded bg-brand-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-800"
            href={`/api/v1/app/counts/${encodeURIComponent(id)}/report.xlsx`}
          >
            Download Excel
          </a>
        </div>
      </div>

      {r.locations.map((l) => {
        const labels = usedLabels(allLabels, [l.quality]);
        const expanded = open === l.location;
        return (
          <Card
            key={l.location}
            title={`${l.location} — ${l.name}`}
            subtitle={`Expected ${l.expected} · found ${l.found} · missing ${l.missing}`}
            actions={
              <div className="flex gap-4">
                <button
                  type="button"
                  className="text-xs font-medium text-brand-700 hover:underline"
                  onClick={() => setVsSql(vsSql === l.location ? null : l.location)}
                >
                  {vsSql === l.location ? 'Hide' : 'Compare with'} SQL
                </button>
                {l.missing > 0 && (
                  <button
                    type="button"
                    className="text-xs font-medium text-brand-700 hover:underline"
                    onClick={() => setOpen(expanded ? null : l.location)}
                  >
                    {expanded ? 'Hide' : 'Show'} missing EPCs
                  </button>
                )}
              </div>
            }
          >
            <div className="mb-4 grid gap-3 sm:grid-cols-4">
              <Stat label="Expected" value={l.expected} />
              <Stat label="Found" value={l.found} />
              <Stat label="Missing" value={l.missing} tone={l.missing > 0 ? 'alert' : undefined} />
              <Stat
                label="Found rate"
                value={l.expected ? `${Math.round((l.found / l.expected) * 100)}%` : '—'}
              />
            </div>
            {labels.length > 0 && (
              <div className="mb-4 flex flex-wrap gap-2 text-xs">
                {labels.map((x) => (
                  <span key={x} className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-700">
                    {x}: <strong>{l.quality[x]}</strong>
                  </span>
                ))}
              </div>
            )}
            {expanded && (
              <p className="mb-4 break-all rounded bg-slate-50 p-3 font-mono text-xs text-slate-600">
                {l.missingEpcs.join(' ')}
              </p>
            )}
            {vsSql === l.location ? (
              <SqlCompare countId={id} location={l.location} />
            ) : (
              <SkuTable lines={l.skus} allLabels={allLabels} />
            )}
          </Card>
        );
      })}

      <Card
        title={`Found, but the system places them elsewhere (${r.elsewhereTags.length})`}
        subtitle="Still in stock at another location (misplaced), or already gone out."
      >
        <Table
          head={['EPC', 'Item', 'System location', 'In stock?', 'Quality']}
          rows={r.elsewhereTags.map((t) => [
            <span className="font-mono text-xs">{t.epc}</span>,
            t.sku,
            t.location,
            t.inStock ? 'yes' : <span className="text-slate-500">no — gone out</span>,
            t.quality,
          ])}
        />
      </Card>

      {r.unknownEpcs.length > 0 && (
        <Card
          title={`Not registered in Smartlink (${r.unknownEpcs.length})`}
          subtitle="Tags read that the system has never been told about."
        >
          <p className="break-all font-mono text-xs text-slate-600">{r.unknownEpcs.join(' ')}</p>
        </Card>
      )}
    </div>
  );
}

type SqlCompareResponse = {
  sql: { source: 'submit' | 'snapshot' | 'none'; label: string };
  rows: Array<{
    item: string;
    description: string | null;
    expected: number;
    scanned: number;
    sql: string | null;
    diff: string | null;
    tagCodes: string | null;
  }>;
  untaggedSql: { items: number; units: string };
};

/** One location per accounting item: RFID expected, scanned, and SQL Accounting's balance. */
function SqlCompare({ countId, location }: { countId: string; location: string }) {
  const [onlyDiff, setOnlyDiff] = useState(false);
  const q = useQuery({
    queryKey: ['epc-count-sql', countId, location],
    queryFn: () =>
      api.get<SqlCompareResponse>(
        `/api/v1/app/counts/${encodeURIComponent(countId)}/locations/${encodeURIComponent(location)}`,
      ),
  });
  if (q.isLoading) return <Loading />;
  if (!q.data) return <Empty>Could not read SQL for {location}.</Empty>;
  const n = (s: string | null) => (s === null ? null : Number(s));
  const rows = q.data.rows.filter((r) => !onlyDiff || (n(r.diff) ?? 0) !== 0);
  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-sm text-slate-600">
        <span>
          {q.data.sql.label}
          {q.data.untaggedSql.items > 0 &&
            ` · SQL also holds ${Number(q.data.untaggedSql.units).toLocaleString()} units of ${q.data.untaggedSql.items} items here with no RFID tag (not listed)`}
        </span>
        <label className="flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={onlyDiff}
            onChange={(e) => setOnlyDiff(e.target.checked)}
          />
          Only differences
        </label>
      </div>
      <Table
        head={[
          'Item',
          'Description',
          'RFID expected',
          'Scanned',
          'System qty',
          'Scanned − System qty',
          'Tag codes',
        ]}
        rows={rows.map((r) => [
          <span className="font-mono text-xs">{r.item}</span>,
          r.description ?? '—',
          cell(r.expected, r.expected),
          cell(r.scanned, r.scanned),
          cell(n(r.sql) ?? 0, r.sql === null ? '—' : n(r.sql)),
          cell(
            n(r.diff) ?? 0,
            r.diff === null ? (
              '—'
            ) : (
              <span className={n(r.diff) !== 0 ? 'font-medium text-alert' : 'text-slate-400'}>
                {n(r.diff)}
              </span>
            ),
          ),
          <span className="text-xs text-slate-500">{r.tagCodes ?? ''}</span>,
        ])}
      />
    </div>
  );
}

function SkuTable({ lines, allLabels }: { lines: SkuLine[]; allLabels: string[] }) {
  const labels = usedLabels(
    allLabels,
    lines.map((s) => s.quality),
  );
  return (
    <Table
      head={['Item', 'Expected', 'Found', 'Missing', ...labels]}
      rows={lines.map((s) => [
        <span className="font-mono text-xs">{s.sku}</span>,
        cell(s.expected, s.expected),
        cell(s.found, s.found),
        cell(
          s.missing,
          <span className={s.missing > 0 ? 'font-medium text-alert' : 'text-slate-400'}>
            {s.missing}
          </span>,
        ),
        ...labels.map((x) => cell(s.quality[x] ?? 0, s.quality[x] ?? '')),
      ])}
    />
  );
}

function Stat({ label, value, tone }: { label: string; value: number | string; tone?: 'alert' }) {
  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
      <div
        className={`mt-1 text-xl font-semibold ${tone === 'alert' ? 'text-alert' : 'text-slate-900'}`}
      >
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  );
}
